import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

export default function LowCreditBanner() {
  const { credits, allocatedCredits, creditsLoaded, user, profile } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [lastDismissedAt, setLastDismissedAt] = useState<number | null>(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("welcome_banner_dismissed") === "true";
    }
    return false;
  });
  const [startingCredits, setStartingCredits] = useState<number | null>(null);
  const [referralCredits, setReferralCredits] = useState<number | null>(null);

  useEffect(() => {
    if (lastDismissedAt !== null && credits < lastDismissedAt) {
      setDismissed(false);
    }
  }, [credits, lastDismissedAt]);

  // Fetch credit config for welcome banner
  useEffect(() => {
    if (!user || !creditsLoaded) return;
    const isNewUser = !profile?.avatar_url;
    if (!isNewUser || welcomeDismissed) return;

    (async () => {
      const { data } = await supabase
        .from("chatbot_settings")
        .select("key, value")
        .in("key", ["signup_starting_credits", "referral_receiver_credits"]);
      if (data) {
        for (const row of data) {
          if (row.key === "signup_starting_credits") setStartingCredits(Number(row.value) || 0);
          if (row.key === "referral_receiver_credits") setReferralCredits(Number(row.value) || 0);
        }
      }
    })();
  }, [user, creditsLoaded, profile?.avatar_url, welcomeDismissed]);

  if (!user || !creditsLoaded) return null;

  // Determine mode
  const isNewUser = !profile?.avatar_url;

  // ── Welcome mode ──
  if (isNewUser && credits > 0 && allocatedCredits > 0 && !welcomeDismissed) {
    const hasReferralBonus = referralCredits && referralCredits > 0 && allocatedCredits > (startingCredits ?? 0);

    return (
      <div className="mx-3 mt-2 mb-1 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/15 p-3 transition-all">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 rounded-full bg-primary/15 p-1.5 flex-shrink-0">
            <Gift className="h-3.5 w-3.5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground/80 leading-relaxed">
              🎉 Welcome! You've been granted{" "}
              <span className="font-bold text-primary">
                ${startingCredits ?? Math.round(credits)} free
              </span>{" "}
              to explore AI features.
              {hasReferralBonus && (
                <>
                  {" "}Plus{" "}
                  <span className="font-bold text-primary">
                    ${referralCredits} referral bonus
                  </span>
                  !
                </>
              )}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setWelcomeDismissed(true);
              localStorage.setItem("welcome_banner_dismissed", "true");
            }}
            className="p-0.5 rounded-full hover:bg-foreground/10 transition-colors flex-shrink-0"
          >
            <X className="h-3 w-3 text-muted-foreground" />
          </button>
        </div>
        <button
          onClick={() => navigate("/profile")}
          className="mt-2 w-full rounded-xl bg-gradient-to-r from-primary to-accent py-2 text-[11px] font-bold text-primary-foreground tracking-wide transition-opacity hover:opacity-90"
        >
          Complete Your Profile
        </button>
      </div>
    );
  }

  // ── Low credit mode ──
  if (dismissed || credits > 1) return null;
  if (credits <= 0 && allocatedCredits === 0) return null;

  const isOverdrawn = credits < 0;
  const isZero = credits === 0;

  const message = isOverdrawn
    ? "Your balance is a little overdrawn — top up to get back on track 💜"
    : isZero
    ? "You've used all your credits! Top up to unlock AI features again 💫"
    : "Credits running low — top up anytime to keep the magic going ✨";

  return (
    <div className="mx-3 mt-2 mb-1 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/15 p-3 transition-all">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 rounded-full bg-primary/15 p-1.5 flex-shrink-0">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-foreground/80 leading-relaxed">
            {message}
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
            setLastDismissedAt(credits);
          }}
          className="p-0.5 rounded-full hover:bg-foreground/10 transition-colors flex-shrink-0"
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      </div>
      <button
        onClick={() => navigate("/premium")}
        className="mt-2 w-full rounded-xl bg-gradient-to-r from-primary to-accent py-2 text-[11px] font-bold text-primary-foreground tracking-wide transition-opacity hover:opacity-90"
      >
        Top Up Credits
      </button>
    </div>
  );
}
